/* =============================================================================
 * bb-delivery-proof.js  —  Delivery-proof social-proof popup (BIG, on-brand)
 * -----------------------------------------------------------------------------
 * ONE bold, tasteful card — black (#0e0e0e) + orange (#EB673C) + big product
 * thumbnail — that turns BarrierBoss's own delivery fleet into social proof.
 *
 *  NEARBY (preferred, when there are recent deliveries near the visitor):
 *     ┌──────────────────────────────────────────────┐ ×
 *     │ 🚚 BARRIERBOSS DELIVERY                       │
 *     │ [ img ]  Peter purchased Corrugated Metal     │
 *     │  84px    Privacy Fence near Naramata          │
 *     │          Delivered near Naramata · 3 days ago │
 *     └──────────────────────────────────────────────┘
 *
 *  STATE FACT (fallback, ALWAYS available when the visitor has geo):
 *     ┌──────────────────────────────────────────────┐ ×
 *     │ 🚚 BARRIERBOSS DELIVERY                       │
 *     │ [ 🚚 ]   This month in California             │
 *     │          100 ordered · 40 delivered           │
 *     └──────────────────────────────────────────────┘
 *
 * Behaviour (per owner spec 2026-07-08, revised):
 *   • Shows EXACTLY ONE popup per session (~6s after load). Slides in bottom-
 *     LEFT, auto-dismisses after ~9s, has an × to dismiss. NO rotation.
 *   • If the visitor CLOSES it (×), it stays gone: a 30-day dismiss is stored in
 *     localStorage (survives new sessions). Auto-hide alone does NOT set that —
 *     only an explicit close does. A per-session "shown" flag stops it re-popping
 *     while navigating within the same session.
 *   • Reuses the shipping estimator's already-resolved geo (sessionStorage
 *     bb_geo_state / bb_geo_city) — polled up to ~9s; bails quietly with no geo.
 *   • Calls the route-dashboard Worker (/deliveries-near), which returns strictly
 *     field-limited proof: nearby `sales[]` (firstName + productTitle +
 *     productImage + town + region + daysAgo + category) AND a `stateFact`
 *     ({ state, ordersCount, deliveriesCount, period }). This script NEVER sees a
 *     surname, street, postal, email, phone or order # — the Worker strips it.
 *   • Prefers a nearby sale; falls back to the state fact so there is ALWAYS
 *     geo-relevant proof to show.
 *   • Bottom-LEFT so it clears Tidio chat + the back-to-top button (bottom-RIGHT).
 *   • Vanilla JS, defer, idempotent. Graceful-empty: no geo / no data -> nothing.
 * ========================================================================== */
(function () {
  'use strict';
  if (window.__bbDeliveryProof) return;         // idempotent
  window.__bbDeliveryProof = true;

  var CFG = window.BB_DELIVERY_PROOF || {};
  var ENDPOINT = CFG.endpoint || 'https://bb-route-dashboard.barrierboss.workers.dev/deliveries-near';
  var STORE = CFG.store === 'ca' ? 'ca' : 'usa';

  var DISMISS_KEY = 'bb_dp_dismissed_until';    // localStorage: epoch ms until which we stay closed
  var SHOWN_KEY = 'bb_dp_shown';                // sessionStorage: already shown once this session
  var DISMISS_DAYS = (CFG.dismissDays || 30);   // × keeps it closed this long
  var APPEAR_MS = (CFG.appearMs || 6000);        // show ~6s after load
  var AUTO_HIDE_MS = (CFG.autoHideMs || 0);   // auto-dismiss after ~9s on screen
  var GEO_WAIT_MS = 9000;                         // poll estimator geo up to ~9s
  var ACCENT = '#EB673C';                        // BarrierBoss orange

  // --- storage guards (fail-open if storage is unavailable) ------------------
  function dismissedActive() {
    try {
      var until = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
      return !!until && Date.now() < until;
    } catch (_) { return false; }
  }
  function shownThisSession() {
    try { return sessionStorage.getItem(SHOWN_KEY) === '1'; } catch (_) { return false; }
  }
  function markShown() { try { sessionStorage.setItem(SHOWN_KEY, '1'); } catch (_) {} }
  function markDismissed() {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now() + DISMISS_DAYS * 864e5)); } catch (_) {}
  }

  if (dismissedActive() || shownThisSession()) return; // done for now

  var START = Date.now();

  // --- estimator geo (it writes these keys) ---------------------------------
  function geo() {
    var s = '', c = '', la = '', ln = '';
    try { s = sessionStorage.getItem('bb_geo_state') || ''; c = sessionStorage.getItem('bb_geo_city') || ''; la = sessionStorage.getItem('bb_geo_lat') || ''; ln = sessionStorage.getItem('bb_geo_lng') || ''; } catch (_) {}
    return { state: s, city: c, lat: la, lng: ln };
  }
  function waitForGeo(cb) {
    var resolving = false;
    (function poll() {
      var g = geo();
      if (g.state) return cb(g);
      if (Date.now() - START > GEO_WAIT_MS) {
        if (resolving) return; resolving = true;
        // No estimator geo this session -> resolve our own (IP) so the popup localises everywhere.
        try {
          fetch('https://ipapi.co/json/').then(function (r) { return r.json(); }).then(function (d) {
            if (d && d.region_code) {
              try {
                sessionStorage.setItem('bb_geo_state', d.region_code);
                if (d.city) sessionStorage.setItem('bb_geo_city', d.city);
                if (d.latitude) sessionStorage.setItem('bb_geo_lat', d.latitude);
                if (d.longitude) sessionStorage.setItem('bb_geo_lng', d.longitude);
              } catch (_) {}
              cb(geo());
            } else { cb(null); }
          }).catch(function () { cb(null); });
        } catch (_) { cb(null); }
        return;
      }
      setTimeout(poll, 300);
    })();
  }

  // --- fetch proof (nearby sales + state fact) -------------------------------
  function fetchProof(g, cb) {
    var qs = 'store=' + encodeURIComponent(STORE) + '&state=' + encodeURIComponent(g.state);
    if (g.city) qs += '&city=' + encodeURIComponent(g.city);
    if (g.lat && g.lng) qs += '&lat=' + encodeURIComponent(g.lat) + '&lng=' + encodeURIComponent(g.lng);
    var url = ENDPOINT + '?' + qs;
    var done = false;
    var to = setTimeout(function () { if (!done) { done = true; cb({ sales: [], stateFact: null }); } }, 6000);
    fetch(url, { method: 'GET', mode: 'cors', credentials: 'omit' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) {
        if (done) return; done = true; clearTimeout(to);
        cb({
          sales: (d && Array.isArray(d.sales)) ? d.sales : [],
          stateFact: (d && d.stateFact && typeof d.stateFact === 'object') ? d.stateFact : null,
        });
      })
      .catch(function () { if (!done) { done = true; clearTimeout(to); cb({ sales: [], stateFact: null }); } });
  }

  // --- copy helpers ----------------------------------------------------------
  function timeAgo(n) {
    if (n == null) return 'recently';
    if (n <= 0) return 'today';
    if (n === 1) return 'yesterday';
    if (n < 7) return 'a few days ago';
    if (n < 60) return 'recently';
    return 'recently';
  }
  function num(n) { n = parseInt(n, 10); return isFinite(n) && n > 0 ? n : 0; }
  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  // --- one-time scoped CSS ---------------------------------------------------
  function injectCSS() {
    if (document.getElementById('bb-dp-style')) return;
    var css = ''
      + '#bb-dp-toast{position:fixed;left:18px;bottom:18px;z-index:2147483000;'
      + 'width:448px;max-width:calc(100vw - 26px);box-sizing:border-box;'
      + 'background:#0e0e0e;color:#fff;border:1px solid #2a2a2a;border-left:4px solid ' + ACCENT + ';'
      + 'border-radius:16px;box-shadow:0 16px 44px rgba(0,0,0,.5);'
      + "font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;"
      + 'padding:15px 16px 16px;transform:translateY(160%);opacity:0;'
      + 'transition:transform .5s cubic-bezier(.2,.85,.25,1),opacity .5s ease;pointer-events:auto}'
      + '#bb-dp-toast.bb-dp-in{transform:translateY(0);opacity:1}'
      + '#bb-dp-toast .bb-dp-eyebrow{display:flex;align-items:center;gap:6px;font-size:10.5px;font-weight:800;'
      + 'letter-spacing:.7px;text-transform:uppercase;color:' + ACCENT + ';margin:0 0 10px}'
      + '#bb-dp-toast .bb-dp-eyebrow .bb-dp-truck{font-size:13px;line-height:1}'
      + '#bb-dp-toast .bb-dp-row{display:flex;align-items:center;gap:14px}'
      + '#bb-dp-toast .bb-dp-thumb{flex:0 0 auto;width:84px;height:84px;border-radius:12px;'
      + 'background:#1a1a1a;overflow:hidden;display:flex;align-items:center;justify-content:center;border:1px solid #2e2e2e}'
      + '#bb-dp-toast .bb-dp-thumb img{width:100%;height:100%;object-fit:cover;display:block}'
      + '#bb-dp-toast .bb-dp-thumb .bb-dp-emo{font-size:38px;line-height:1}'
      + '#bb-dp-toast .bb-dp-body{flex:1 1 auto;min-width:0}'
      + '#bb-dp-toast .bb-dp-lead{font-size:15.5px;font-weight:600;line-height:1.34;margin:0;color:#f2f2f2;'
      + 'display:-webkit-box;-webkit-line-clamp:3;-webkit-box-orient:vertical;overflow:hidden}'
      + '#bb-dp-toast .bb-dp-lead b{font-weight:800;color:#fff}'
      + '#bb-dp-toast .bb-dp-lead .bb-dp-prod{color:' + ACCENT + ';font-weight:800}'
      + '#bb-dp-toast .bb-dp-meta{font-size:12px;color:#b9b9b9;margin-top:7px;line-height:1.3}'
      + '#bb-dp-toast .bb-dp-meta .bb-dp-dot{color:#555;margin:0 6px}'
      // state-fact variant
      + '#bb-dp-toast .bb-dp-period{font-size:12px;font-weight:700;color:#cfcfcf;margin:0 0 4px;'
      + 'text-transform:uppercase;letter-spacing:.4px}'
      + '#bb-dp-toast .bb-dp-stat{font-size:17px;font-weight:600;line-height:1.3;margin:0;color:#f2f2f2}'
      + '#bb-dp-toast .bb-dp-stat .bb-dp-n{font-size:22px;font-weight:900;color:' + ACCENT + '}'
      + '#bb-dp-toast .bb-dp-stat .bb-dp-dot{color:#555;margin:0 7px}'
      + '#bb-dp-toast .bb-dp-x{position:absolute;top:10px;right:12px;background:none;border:0;cursor:pointer;'
      + 'font-size:19px;line-height:1;color:#7a7a7a;padding:4px}'
      + '#bb-dp-toast .bb-dp-x:hover{color:#fff}'
      // MOBILE (≤600px): SHORT + WIDE card. All !important to beat the base #id .class
      // rules (same specificity → force it). Wider (right stops ~70px short, clears the
      // chat/back-to-top bottom-right), 40px thumb, single-line lead, tight padding.
      + '@media (max-width:600px){'
      + '#bb-dp-toast{left:8px!important;right:70px!important;width:auto!important;max-width:none!important;padding:8px 11px 9px!important;border-radius:13px!important}'
      + '#bb-dp-toast .bb-dp-row{gap:9px!important;align-items:center!important}'
      + '#bb-dp-toast .bb-dp-thumb{width:40px!important;height:40px!important;min-width:40px!important;flex:0 0 40px!important;border-radius:8px!important}'
      + '#bb-dp-toast .bb-dp-thumb .bb-dp-emo{font-size:19px!important}'
      + '#bb-dp-toast .bb-dp-eyebrow{margin:0 0 3px!important;font-size:9px!important;letter-spacing:.4px!important}'
      + '#bb-dp-toast .bb-dp-lead{font-size:12.5px!important;line-height:1.24!important;-webkit-line-clamp:2!important}'
      + '#bb-dp-toast .bb-dp-meta{font-size:10px!important;margin-top:2px!important}'
      + '#bb-dp-toast .bb-dp-period{font-size:10px!important;margin-bottom:2px!important}'
      + '#bb-dp-toast .bb-dp-stat{font-size:13px!important}'
      + '#bb-dp-toast .bb-dp-stat .bb-dp-n{font-size:16px!important}'
      + '#bb-dp-toast .bb-dp-x{top:5px!important;right:8px!important;font-size:16px!important}'
      + '}'
      + '@media (prefers-reduced-motion:reduce){#bb-dp-toast{transition:opacity .3s ease}}';
    var st = document.createElement('style');
    st.id = 'bb-dp-style';
    st.textContent = css;
    document.head.appendChild(st);
  }

  // --- render ----------------------------------------------------------------
  var el, timer;

  function thumbImg(url) {
    return '<span class="bb-dp-thumb"><img src="' + esc(url) + '" alt="" ' +
      'onerror="this.parentNode.innerHTML=&quot;<span class=\\&quot;bb-dp-emo\\&quot;>&#128666;</span>&quot;"></span>';
  }
  function thumbEmoji() {
    return '<span class="bb-dp-thumb"><span class="bb-dp-emo" aria-hidden="true">&#128666;</span></span>';
  }
  var EYEBROW = '<div class="bb-dp-eyebrow"><span class="bb-dp-truck" aria-hidden="true">&#128666;</span> Recent orders in your area</div>';

  function nearbyHTML(d) {
    var town = esc(d.town || '');
    var lead = '<b>' + esc(d.firstName) + '</b> purchased ' +
      '<span class="bb-dp-prod">' + esc(d.productTitle) + '</span>' +
      (town ? ' near <b>' + town + '</b>' : '');
    var meta = 'Delivered' + (town ? ' near ' + town : '') +
      '<span class="bb-dp-dot">&middot;</span>' + esc(timeAgo(d.daysAgo));
    return EYEBROW +
      '<div class="bb-dp-row">' + (d.productImage ? thumbImg(d.productImage) : thumbEmoji()) +
        '<div class="bb-dp-body">' +
          '<p class="bb-dp-lead">' + lead + '</p>' +
          '<div class="bb-dp-meta">' + meta + '</div>' +
        '</div>' +
      '</div>';
  }

  function stateHTML(f) {
    var oc = num(f.ordersCount), dc = num(f.deliveriesCount);
    var period = esc(f.period || 'this month');
    var stateName = esc(f.state || '');
    var pieces = [];
    /* ⛔ 2026-10-30: never advertise a zero. pickCard() only calls this when at least one
       count is > 0, so pieces is never empty. */
    if (oc > 0) pieces.push('<span class="bb-dp-n">' + oc + '</span> ' + (oc === 1 ? 'customer' : 'customers') + ' ordered');
    if (dc > 0) pieces.push('<span class="bb-dp-n">' + dc + '</span> ' + (dc === 1 ? 'delivery' : 'delivered'));
    return EYEBROW +
      '<div class="bb-dp-row">' + thumbEmoji() +
        '<div class="bb-dp-body">' +
          '<p class="bb-dp-period">' + period + (stateName ? ' in ' + stateName : '') + '</p>' +
          '<p class="bb-dp-stat">' + pieces.join('<span class="bb-dp-dot">&middot;</span>') + '</p>' +
        '</div>' +
      '</div>';
  }

  function build(html) {
    injectCSS();
    el = document.createElement('div');
    el.id = 'bb-dp-toast';
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    el.innerHTML = '<button class="bb-dp-x" type="button" aria-label="Dismiss">&times;</button>' + html;
    el.querySelector('.bb-dp-x').addEventListener('click', dismiss);
    document.body.appendChild(el);
  }

  function showOnce(html) {
    markShown(); // exactly one per session, full stop
    build(html);
    requestAnimationFrame(function () { el.classList.add('bb-dp-in'); });
    if (AUTO_HIDE_MS > 0) { timer = setTimeout(function () { hide(); }, AUTO_HIDE_MS); }
  }
  function hide() {
    clearTimeout(timer);
    if (!el) return;
    el.classList.remove('bb-dp-in');
    setTimeout(function () { if (el && el.parentNode) el.parentNode.removeChild(el); el = null; }, 520);
  }
  function dismiss() {
    markDismissed(); // × -> stays closed for DISMISS_DAYS (localStorage)
    hide();
  }

  // Choose the single card: freshest valid nearby sale, else the state fact.
  function pickCard(res) {
    var sales = res.sales || [];
    /* Prefer the freshest sale that actually HAS a product photo. Some historical orders point at
       products that were since renamed or retired, so the feed has no image for them and the card
       fell back to the truck placeholder. The list is freshest-first and long, so this costs
       nothing in recency. A DIFFERENT product's photo was rejected - it would caption a real
       purchase with the wrong item. */
    var valid = function (d) { return d && d.firstName && d.productTitle; };
    for (var i = 0; i < sales.length; i++) {
      if (valid(sales[i]) && sales[i].productImage) return nearbyHTML(sales[i]);
    }
    for (var j = 0; j < sales.length; j++) {
      if (valid(sales[j])) return nearbyHTML(sales[j]);
    }
    var f = res.stateFact;
    if (f && (num(f.ordersCount) > 0 || num(f.deliveriesCount) > 0)) return stateHTML(f);
    return null;
  }

  // --- boot ------------------------------------------------------------------
  function boot() {
    if (dismissedActive() || shownThisSession()) return;
    waitForGeo(function (g) {
      if (!g || !g.state) return;                        // no geo -> bail
      fetchProof(g, function (res) {
        var html = pickCard(res);
        if (!html) return;                                // no data -> bail
        var wait = Math.max(0, APPEAR_MS - (Date.now() - START)); // honour ~6s floor
        setTimeout(function () {
          if (dismissedActive() || shownThisSession()) return; // re-check at fire time
          showOnce(html);
        }, wait);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
